// src/features/comunicacao/hooks/useBrowserMentionIndicator.ts
import { useEffect, useRef } from "react";

type Options = {
  titleBase?: string;   // título padrão (sem contador)
  faviconHref?: string; // favicon base (sem badge)
};

// desenha um favicon com bolinha/vermelho e número (fallback visual)
function drawFaviconBadge(baseHref: string, count: number, onReady: (dataUrl: string)=>void) {
  const size = 32;
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = baseHref;
  img.onload = () => {
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(img, 0, 0, size, size);
    const r = 10;
    ctx.beginPath();
    ctx.arc(size - r, r, r, 0, Math.PI * 2);
    ctx.fillStyle = "#ef4444";
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 12px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(count > 99 ? "99+" : count), size - r, r + 0.5);
    onReady(canvas.toDataURL("image/png"));
  };
}

function setFaviconHref(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

export function useBrowserMentionIndicator(mentionCount: number, opts: Options = {}) {
  const { titleBase = document.title || "ACOR ERP", faviconHref } = opts;
  const baseTitleRef = useRef(titleBase);
  const baseIconRef = useRef<string | null>(null);

  useEffect(() => {
    if (!baseIconRef.current) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      baseIconRef.current = link?.href || faviconHref || "/favicon.ico";
    }
  }, [faviconHref]);

  useEffect(() => {
    try {
      // Badging API (Chrome/Edge/Android)
      if ('setAppBadge' in navigator) {
        if (mentionCount > 0) {
          // @ts-ignore
          navigator.setAppBadge(mentionCount);
        } else {
          // @ts-ignore
          navigator.clearAppBadge?.();
        }
      } else {
        document.title = mentionCount > 0 ? `(${mentionCount}) ${baseTitleRef.current}` : baseTitleRef.current;
      }
    } catch {
      document.title = mentionCount > 0 ? `(${mentionCount}) ${baseTitleRef.current}` : baseTitleRef.current;
    }

    // Favicon com "bolinha" (fallback visual)
    const base = baseIconRef.current!;
    if (mentionCount > 0) {
      drawFaviconBadge(base, mentionCount, (dataUrl) => setFaviconHref(dataUrl));
    } else {
      setFaviconHref(base);
    }

    const previousTitle = baseTitleRef.current;
    const previousIcon = baseIconRef.current;

    return () => {
      // restaura ao desmontar
      document.title = previousTitle;
      setFaviconHref(previousIcon || "/favicon.ico");
      // @ts-ignore
      navigator.clearAppBadge?.();
    };
  }, [mentionCount]);
}
